import { handleAppError } from '@/lib/errorUtils';
import { APP_ERRORS } from '@/constants/errors';

export async function fetchJobDescriptionFromUrl(url: string, searchJobDescriptionWithAI: (link: string) => Promise<{text: string, usage: any}>, cleanJobDescriptionWithAI?: (text: string) => Promise<{text: string, usage: any}>): Promise<{ text: string, cost: number, promptTokens: number, completionTokens: number }> {
  let text = '';
  let cost = 0;
  let promptTokens = 0;
  let completionTokens = 0;

  if (url.includes('linkedin.com')) {
    const res = await searchJobDescriptionWithAI(url);
    text = res.text;
    if (res.usage) {
      cost += res.usage.cost;
      promptTokens += res.usage.promptTokens;
      completionTokens += res.usage.completionTokens;
    }
    if (!text || text.trim().length < 50) {
      throw new Error(APP_ERRORS.AI_SEARCH_INVALID_JD.message);
    }
    const aiResponseLower = text.toLowerCase().trim();
    if (aiResponseLower.startsWith('i am sorry') || aiResponseLower.startsWith('i apologize') || aiResponseLower.startsWith('i cannot') || aiResponseLower.startsWith('i am unable') || aiResponseLower.startsWith("i'm sorry") || aiResponseLower.startsWith("i can't") || aiResponseLower.includes('unable to extract')) {
       throw new Error(APP_ERRORS.URL_FETCH_FAILED.message);
    }
    return { text, cost, promptTokens, completionTokens };
  }

  let targetUrl = url;
  if (url.includes('ashby_jid=')) {
    const urlObj = new URL(url);
    const jid = urlObj.searchParams.get('ashby_jid');
    let hostname = urlObj.hostname;
    hostname = hostname.replace(/^www\./, '').split('.')[0];
    if (jid && hostname) {
      targetUrl = `https://jobs.ashbyhq.com/${hostname}/${jid}`;
    }
  }

  const fetchWithTimeout = async (fetchUrl: string, timeout = 8000) => {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeout);
    try {
      const response = await fetch(fetchUrl, { signal: controller.signal });
      clearTimeout(id);
      return response;
    } catch (error) {
      clearTimeout(id);
      throw error;
    }
  };

  let htmlContent = '';
  try {
    const jinaRes = await fetchWithTimeout(`https://r.jina.ai/${targetUrl}`, 15000);
    if (!jinaRes.ok) throw new Error('jina failed');
    text = await jinaRes.text();
    
    // If the text is quite long, and we have the clean function, let's clean it up
    if (text.length > 3000 && cleanJobDescriptionWithAI) {
      try {
        const cleanRes = await cleanJobDescriptionWithAI(text);
        if (cleanRes && cleanRes.text && cleanRes.text.length > 50) {
          text = cleanRes.text;
          if (cleanRes.usage) {
            cost += cleanRes.usage.cost || 0;
            promptTokens += cleanRes.usage.promptTokens || 0;
            completionTokens += cleanRes.usage.completionTokens || 0;
          }
        }
      } catch (cleanError) {}
    }
  } catch (e0) {
    try {
      const res1 = await fetchWithTimeout(`https://api.allorigins.win/get?url=${encodeURIComponent(targetUrl)}`);
      if (!res1.ok) throw new Error('allorigins failed');
      const data = await res1.json();
      htmlContent = data.contents;
    } catch (e1) {
      try {
        const res2 = await fetchWithTimeout(`https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(targetUrl)}`);
        if (!res2.ok) throw new Error('codetabs failed');
        htmlContent = await res2.text();
      } catch (e2) {}
    }
    
    if (htmlContent) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, 'text/html');
      doc.querySelectorAll('script, style, nav, footer, header').forEach(el => el.remove());
      
      text = doc.body.textContent || '';
      text = text.replace(/\s+/g, ' ').trim();
    }
  }

  const lowerText = text.toLowerCase();
  const isUnreachable = lowerText.includes('cloudflare') || 
                        lowerText.includes('access denied') || 
                        text.length < 300 ||
                        (!lowerText.includes('responsibilit') && !lowerText.includes('requirement') && !lowerText.includes('experience') && !lowerText.includes('qualific') && !lowerText.includes('what you'));

  if (!text || isUnreachable) {
    const res = await searchJobDescriptionWithAI(targetUrl);
    text = res.text;
    if (res.usage) {
      cost += res.usage.cost;
      promptTokens += res.usage.promptTokens;
      completionTokens += res.usage.completionTokens;
    }
    if (!text || text.trim().length < 50) {
      throw new Error(APP_ERRORS.AI_SEARCH_INVALID_JD.message);
    }
    const aiResponseLower = text.toLowerCase().trim();
    if (aiResponseLower.startsWith('i am sorry') || aiResponseLower.startsWith('i apologize') || aiResponseLower.startsWith('i cannot') || aiResponseLower.startsWith('i am unable') || aiResponseLower.startsWith("i'm sorry") || aiResponseLower.startsWith("i can't") || aiResponseLower.includes('unable to extract')) {
       throw new Error(APP_ERRORS.URL_FETCH_FAILED.message);
    }
  }

  return { text, cost, promptTokens, completionTokens };
}
